import { find, maxBy, pickBy, inRange } from 'lodash';
import { SCORE, CountType } from 'common/constants';
import { CardFacadeBase } from 'utils/facades/card-facade-base';
import { location } from 'utils/url-helper';
import type { CardFlowEvent, StorySettingsType } from 'types/story';
import { type TotalScoreType, type TotalCharacterPointsType, StoryHistory } from 'client/utils';
import { StoryFacade } from './story-facade';

type GetNextEventByScore = (score: TotalScoreType) => CardFlowEvent | null | undefined;
type GetNextEventByResultPoints = (score: TotalCharacterPointsType) => CardFlowEvent | null | undefined;
type GetNextEventByRate = (story: StoryFacade) => CardFlowEvent | null | undefined;
type GetNextEventByUrlParam = () => CardFlowEvent | null | undefined;
type GetNextEventByRedirect = () => CardFlowEvent | null | undefined;
type GetNextEventByGptTextResponse = (result: boolean) => CardFlowEvent | null | undefined;
type GetNextEventByGeolocation = (countryIso2?: string) => CardFlowEvent | null | undefined;

class NavigationFacade extends CardFacadeBase {
	private getNextEventByScore: GetNextEventByScore = ({ scoreRelative: totalScore }) => {
		if (!this.data) {
			return null;
		}

		return find(this.data.events, ({ score }) => {
			if (!score) {
				return false;
			}
			const min = score[0] || SCORE.RANGE.MIN;
			const max = Number.isInteger(score[1]) ? score[1] + 1 : Infinity;
			return inRange(totalScore, min, max);
		});
	};

	private getNextEventByCharacterPoints: GetNextEventByResultPoints = totalCharacterPoints => {
		const { characters } = this;
		const totalCardCharacterPoints = pickBy(totalCharacterPoints, (value, key) => key in characters);
		const [characterId] = maxBy(Object.entries(totalCardCharacterPoints), ([, value]) => value) ?? [];
		return characterId === undefined ? null : find(this.data.events, o => o._id === characterId);
	};

	private getNextEventByUrlParam: GetNextEventByUrlParam = () => {
		if (!this.data || !this.data.settings.urlNavigationParam) {
			return null;
		}

		const urlParam = this.data.settings.urlNavigationParam;
		const currentUrlParams = new URL(window.location.href).searchParams;
		const targetValue = currentUrlParams.get(urlParam);
		const defaultEvent = this.data.events.find(e => e.isDefault);

		return this.data.events.find(e => e.name === targetValue) ?? defaultEvent ?? null;
	};

	private getNextEventByGeolocation: GetNextEventByGeolocation = countryIso2 => {
		if (!countryIso2) {
			return null;
		}

		const defaultEvent = this.data.events.find(e => e.isDefault);

		return (
			this.data.events.find(e => e.countryIso2?.toLowerCase() === countryIso2.toLowerCase()) ??
			defaultEvent ??
			null
		);
	};

	private getNextEventByRedirect: GetNextEventByRedirect = () => {
		if (!this.data || !this.data.settings.redirectUrl) {
			return null;
		}

		return {
			_id: 'redirect',
			name: 'Redirect',
			redirectUrl: this.data.settings.redirectUrl,
		};
	};

	private getNextEventByRate: GetNextEventByRate = storyFacade => {
		if (!this.data) {
			return null;
		}

		const { isPreview } = location.client;
		const storyHistory = !isPreview ? new StoryHistory({ storyId: storyFacade.storyId }) : null;
		const savedRate = storyHistory?.getCardABRate(this.data._id);
		const rate = savedRate ?? Math.round(Math.random() * 100);

		if (!isPreview && savedRate === undefined && storyHistory) {
			storyHistory.addABRate({ cardId: this.data._id, rate });
		}

		const sortedEvents = this.data.events.filter(e => e.rate).sort((a, b) => a.rate! - b.rate!); // ascending order
		let accumulatedRate = 0;
		let result: CardFlowEvent | null = null;

		sortedEvents.forEach(event => {
			accumulatedRate += event.rate!;
			if (!result && rate <= accumulatedRate) {
				result = event;
			}
		});

		return result;
	};

	private getNextEventByGptTextResponse: GetNextEventByGptTextResponse = (result: boolean) => {
		if (!this.data) {
			return null;
		}

		return result
			? this.data.events.find(e => e.isDefault) ?? null
			: this.data.events.find(e => e.isDefault !== true) ?? null;
	};

	get nextEvent() {
		return {
			[CountType.score]: this.getNextEventByScore,
			[CountType.character]: this.getNextEventByCharacterPoints,
			[CountType.ab]: this.getNextEventByRate,
			[CountType.urlParam]: this.getNextEventByUrlParam,
			[CountType.redirect]: this.getNextEventByRedirect,
			[CountType.gptTextResponse]: this.getNextEventByGptTextResponse,
			[CountType.geolocation]: this.getNextEventByGeolocation,
		};
	}

	get characters() {
		return this.data.events.reduce(
			(acc, event) => {
				acc[event._id] = { name: event.name, cardId: this.data._id };
				return acc;
			},
			{} as Exclude<StorySettingsType['characters'], undefined>
		);
	}

	getCountType = (storySettings: StorySettingsType) => {
		return storySettings.cards?.[this.data._id]?.countType ?? CountType.score;
	};
}

export { NavigationFacade };
